import React, { useEffect, useState } from "react";
import { AutoComplete, Input, Typography } from "antd";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";
import { Colors } from "../helpers";
import { useCallHistoryProvider } from "../context/CallHIstoryContext";
import { useTheme } from "../../../ThemeContext";
import "../CallHistory.css";
import { Chip, Grid, Tooltip } from "@mui/material";
import SortIcon from "@mui/icons-material/Sort";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

const { Text } = Typography;

// const SearchIcon = <SearchOutlined style={{ fontSize: 18, color: Colors.text }} />;

const activeMenuButtonStyle = {
  backgroundColor: "#ff4e3a",
  color: "#ffffff",
  borderRadius: "1vh",
  marginBottom: "1vh",
  cursor: "pointer",
  justifyContent: "center",
};

export const SearchInput = ({
  placeholder,
  searchInput,
  setSearchInput,
  className,
  width
}) => {
  const { themeMode, currentTheme } = useTheme();

  return (
    <div
      style={{
        borderRadius: "1vh",
        backgroundColor: currentTheme.callhistory.searchTranscriptColor,
        color: currentTheme.callhistory.searchColor,
        width: width ? width : '20rem'
      }}
    >
      <div
        style={{ display: "flex", alignItems: "left", justifyContent: "left", width: width ? width : '20rem' }}
      >
        <SearchOutlined
          style={{
            fontSize: 18,
            color: currentTheme.callhistory.searchColor,
            marginRight: 8,
            marginLeft: 6,
          }}
        />
        <input
          placeholder={placeholder}
          className={`call-history__searchInput ${className || ""}`}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{
            color: currentTheme.callhistory.searchColor,
            height: "2vh",
            width: width ? width : '20rem'
          }}
        />
      </div>
    </div>
  );
};

const searchResult = (data, currentTheme) => {
  return data?.map((item, index) => {
    // Always show member_name and member_id
    const displayName = `${item.member_name} (ID: ${item.member_id})`;
    
    return {
      value: displayName,
      id: item.member_id,
      callCount: item.callCount,
      originalName: item.member_name,
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: currentTheme.callhistory.bgLite,
            cursor: "pointer",
            padding: "8px 12px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
          <Text
            style={{ color: currentTheme.callhistory.textLight, fontSize: 14 }}
          >
            {item.member_name}
          </Text>
          <Text
            style={{ 
              color: currentTheme.callhistory.textLight, 
              fontSize: 11,
              opacity: 0.7
            }}
          >
            ID: {item.member_id}
          </Text>
          </div>
          <Chip
            label={`${item.callCount} call${item.callCount !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
            style={{
              fontSize: "11px",
              height: "20px",
              color: currentTheme.callhistory.textLight,
              borderColor: currentTheme.callhistory.textLight
            }}
          />
        </div>
      ),
    };
  });
};

export const AutoCompleteInput = ({
  className,
  setLoading,
  setTotalRows,
  setRenderType,
  setCurrentPage,
  setPrevPageState
}) => {
  const [options, setOptions] = useState([]);
  const {
    callerList,
    callList,
    setSelectedMember,
    selectedMember,
    setFilterType,
    filterType,
    filterBy,
    setFilterBy,
    sortCalls,
    filterCallsByMember,
    getMemberCalls,
    getMemberCallCount,
    setSelectedCall,
    setCallStatusFilter
  } = useCallHistoryProvider();
  const { themeMode, currentTheme } = useTheme();
  const [selectedText, setSelectedText] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);

  console.log("callerList", callerList);
  
  // Update the display when a member is selected in the context
  useEffect(() => {
    if (selectedMember && callerList.length > 0) {
      const member = callerList.find(item => item.member_id === selectedMember);
      if (member) {
        setSelectedText(member.member_name || member.memberName || '');
      }
    } else if (!selectedMember) {
      setSelectedText('');
    }
  }, [selectedMember, callerList]);

  const [showFilters, setShowFilters] = useState(false);

  const handleFilterChange = () => {
    if (filterBy === "date") {
      setFilterBy("name");
    } else {
      setFilterBy("date");
    }
    // Apply sorting immediately when filter changes
    applySorting();
  };

  const handleFilterTypeChange = () => {
    if (filterType === "ASC") {
      setFilterType("DESC");
    } else {
      setFilterType("ASC");
    }
    // Apply sorting immediately when filter type changes
    applySorting();
  };

  const applySorting = () => {
    const sortBy = filterBy === "name" ? "member_name" : "call_start_time";
    sortCalls(sortBy, filterType);
    setCurrentPage(1); // Reset to first page after sorting
  };

  const darkInactiveMenuButtonStyle = {
    ...activeMenuButtonStyle,
    backgroundColor: "#3e3c57",
    color: currentTheme.homePage.textColor,
  };

  const lightInactiveMenuButtonStyle = {
    ...activeMenuButtonStyle,
    backgroundColor: "#e3e2e5",
    color: currentTheme.homePage.textColor,
  };

  const handleSearch = (value) => {
    setSelectedText(value);
    
    // Don't process if callerList is empty
    if (!callerList || callerList.length === 0) {
      setOptions([]);
      return;
    }
    
    // If input is focused but empty, show all members
    if (!value && isInputFocused) {
      showAllMembers();
    } else if (!value) {
      setOptions([]);
    } else {
      // First, get all matching members
      const matchingMembers = callerList
        .map((item) => ({
          member_id: item.member_id,
          member_name: item.member_name || item.memberName,
          callCount: item.callCount || getMemberCallCount(item.member_id) || 0
        }))
        .filter((item) => {
          const searchVal = value.toLowerCase();
          return (
            item.member_name.toLowerCase().includes(searchVal) ||
            (item.member_id && item.member_id.toLowerCase().includes(searchVal))
          );
        });

      // Count duplicates by name
      const nameCount = {};
      matchingMembers.forEach(member => {
        nameCount[member.member_name] = (nameCount[member.member_name] || 0) + 1;
      });

      // Add duplicate count to each member
      const dataWithDuplicates = matchingMembers.map(member => ({
        ...member,
        duplicateCount: nameCount[member.member_name]
      }));

      // Sort by call count descending
      const sortedData = dataWithDuplicates.sort((a, b) => b.callCount - a.callCount);
      
      setOptions(
        sortedData.length > 0
          ? searchResult(sortedData, currentTheme)
          : [{ value: '', label: <span style={{ color: currentTheme.callhistory.textLight, opacity: 0.7 }}>No data found</span>, disabled: true }]
      );
    }
  };

  const showAllMembers = () => {
    // Don't process if callerList is empty
    if (!callerList || callerList.length === 0) {
      setOptions([]);
      return;
    }
    
    // Show all members when dropdown is opened
    const allMembers = callerList.map((item) => ({
      member_id: item.member_id,
      member_name: item.member_name || item.memberName,
      callCount: item.callCount || getMemberCallCount(item.member_id) || 0
    }));

    // Count duplicates by name
    const nameCount = {};
    allMembers.forEach(member => {
      nameCount[member.member_name] = (nameCount[member.member_name] || 0) + 1;
    });

    // Add duplicate count to each member
    const dataWithDuplicates = allMembers.map(member => ({
      ...member,
      duplicateCount: nameCount[member.member_name]
    }));

    // Sort by call count descending
    const sortedData = dataWithDuplicates.sort((a, b) => b.callCount - a.callCount);
    
    setOptions(searchResult(sortedData, currentTheme));
  };

  const onSelect = (value, option) => {
    const id = option?.id;
    const callCount = option?.callCount || 0;
    const originalName = option?.originalName || value;
    
    // Set the display text to the original name (without ID suffix)
    setSelectedText(originalName);
    
    // Clear the dropdown options and reset focus state
    setOptions([]);
    setIsInputFocused(false);
    
    console.log("Selected member:", id, "with", callCount, "calls");

    if (id) {
      setSelectedMember(id);
      setCurrentPage(1); // Reset to first page when selecting a member
      
      // Apply member filtering immediately
      filterCallsByMember(id);
      
      // Update total rows based on member's calls
      const memberCalls = getMemberCalls(id);
      setTotalRows(memberCalls.length);
      
      console.log(`Filtered to show ${memberCalls.length} calls for member: ${originalName} (ID: ${id})`);
    }
  };

  const handleFilterSearch = () => {
    setCurrentPage(1);
    applySorting();
  };

  const handleReset = () => {
    console.log("Resetting all filters and showing all calls...");
    
    // Reset all state variables first
    setCurrentPage(1);
    setSelectedText('');
    setOptions([]); // Clear dropdown options
    setIsInputFocused(false); // Reset focus state
    setFilterBy('date');
    setFilterType('DESC');
    setCallStatusFilter('all'); // Reset status filter
    
    // Clear selected member and selected call first
    setSelectedMember('');
    setSelectedCall('');
    
    // Reset member filtering to show all calls
    filterCallsByMember('');
  };

  return (
    <>
      <AutoComplete
        popupMatchSelectWidth={"100%"}
        style={{ width: "100%", color: currentTheme.callhistory.text }}
        options={options}
        onSelect={onSelect}
        onSearch={handleSearch}
        size="large"
        value={selectedText}
        open={options.length > 0} // Show dropdown when there are options
        dropdownStyle={{
          background: currentTheme.callhistory.bgLite,
          color: currentTheme.callhistory.text,
        }}
        onFocus={() => {
          setIsInputFocused(true);
          // Show all members when input is focused and empty
          if (!selectedText) {
            showAllMembers();
          }
        }}
        onBlur={() => {
          setIsInputFocused(false);
          // Clear options when clicking outside after a short delay
          setTimeout(() => {
            if (!selectedMember) {
              setOptions([]);
            }
          }, 200);
        }}
      >
        <div
          style={{
            borderRadius: "1vh",
            backgroundColor: currentTheme.callhistory.searchBackground,
            color: currentTheme.callhistory.searchColor,
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <SearchOutlined
              style={{
                fontSize: 18,
                color: currentTheme.callhistory.searchColor,
                marginLeft: "15px",
              }}
            />
            <input
              className={`call-history__searchInput ${className || ""}`}
              placeholder={selectedMember ? `Showing calls for: ${selectedText}` : "Click to see all members or type to search"}
              value={selectedText}
              onChange={(e) => {
                const newValue = e.target.value;
                handleSearch(newValue); // Use handleSearch instead of just setSelectedText
                
                // If user clears the input and there was a selected member, reset everything
                if (!newValue && selectedMember) {
                  handleReset();
                }
              }}
              onFocus={() => {
                setIsInputFocused(true);
                // Show all members when input is focused and empty
                if (!selectedText) {
                  showAllMembers();
                }
              }}
              onBlur={() => {
                setIsInputFocused(false);
                // Clear options when clicking outside after a short delay
                setTimeout(() => {
                  if (!selectedMember) {
                    setOptions([]);
                  }
                }, 200);
              }}
              style={{
                color: currentTheme.callhistory.searchColor,
                width: "100%", // Adjust width as needed
                fontWeight: selectedMember ? "600" : "normal"
              }}
            />
            {selectedMember && (
              <Tooltip title="Clear member filter">
                <CloseOutlined
                  style={{
                    fontSize: 16,
                    color: currentTheme.callhistory.searchColor,
                    marginRight: "8px",
                    cursor: "pointer",
                    padding: "2px"
                  }}
                  onClick={handleReset}
                />
              </Tooltip>
            )}
            <Tooltip title="sort">
              <Chip
                icon={showFilters ? <ArrowUpwardIcon /> : <SortIcon />}
                size="small"
                variant="contained"
                onClick={() => setShowFilters(!showFilters)}
              />
            </Tooltip>
          </div>
        </div>
      </AutoComplete>
      {showFilters && (
        <Grid sx={{ display: "flex", justifyContent: "space-evenly" }}>
          <Tooltip
            title={`Click to sort by ${filterBy === "name" ? "Date" : "Name"}`}
          >
            <Chip
              label={`Sorted By ${filterBy}`}
              size="small"
              color="primary"
              variant="outlined"
              onClick={handleFilterChange}
            />
          </Tooltip>
          <Tooltip
            title={`Click to sort ${filterBy} in ${
              filterType === "DESC" ? "ASC" : "DESC"
            } order`}
          >
            <Chip
              label={`Sorted in ${filterType} order`}
              size="small"
              color="primary"
              variant="outlined"
              onClick={handleFilterTypeChange}
            />
          </Tooltip>
          <Tooltip title="Apply filter">
            <Chip
              label="Apply"
              size="small"
              color="primary"
              variant="contained"
            onClick={handleFilterSearch}
            />
          </Tooltip>
          <Tooltip title="Reset filters">
            <Chip
              label="Reset"
              size="small"
              color="primary"
              variant="contained"
            onClick={handleReset}
          />
          </Tooltip>
        </Grid>
      )}
    </>
  );
};

